import { Button, Card, Image, Modal, Pagination, Table } from 'react-bootstrap'
import { Description, Td, Title, XStack, YStack } from './index.style'
import { arrayOfIntegers, uploadImage } from './utils'
import { createHighlightSeries, updateHighlightSeries } from './data'
import { useCallback, useState } from 'react'

import CountdownStatus from './components/CountdownStatus'
import FormHighlightLottery from './components/FormHighlightLottery'
import FormUpdateHighlightLottery from './components/FormUpdateHighlightLottery'
import Swal from 'sweetalert2'
import TagStatus from './components/TagStatus'
import moment from 'moment'
import { useHighlightSeriesList } from './hooks/useHighlightSeriesList'
import { useToggle } from './hooks/useToggle'

const HighlightLottery = () => {
  const [pageParam, setPageParam] = useState(1)
  const { highlightSeriesList, data, loading, refetch } = useHighlightSeriesList({ pageParam })
  const [onFormCreate, toggleFormCreate] = useToggle(false)
  const [onFormUpdate, toggleFormUpdate] = useToggle(false)
  const [highlightId, setHighlightId] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmitFormCreate = useCallback(
    async (formValues) => {
      setIsLoading(true)
      try {
        const sizeFormat = arrayOfIntegers(formValues.size)
        const imageUrl = formValues.image ? await uploadImage(formValues.image) : null
        const schedule = moment(formValues.start).subtract(1, 'minutes').toISOString()

        const response = await createHighlightSeries({
          start: new Date(formValues.start).toISOString(),
          end: new Date(formValues.end).toISOString(),
          sizes: sizeFormat,
          headerImage: imageUrl ?? '',
          title: formValues.title ?? '',
          description: formValues.describe ?? '',
          showCountdown: formValues.showCountdown ?? false,
          schedule: new Date(schedule).toISOString(),
        })

        if (response.status.code !== 0) {
          throw new Error(response?.status?.description)
        }

        toggleFormCreate()
        Swal.fire({
          icon: 'success',
          title: 'บันทึกการตั้งค่าสำเร็จ',
        })

        if (pageParam > 1) {
          setPageParam(1)
        } else {
          refetch()
        }
      } catch (error) {
        handleError(error)
      } finally {
        setIsLoading(false)
      }
    },
    [refetch, pageParam, toggleFormCreate],
  )

  const handleSubmitFormUpdate = useCallback(
    async (formValues) => {
      setIsLoading(true)
      try {
        const response = await updateHighlightSeries({
          id: formValues.id,
          remark: formValues.remark,
        })

        if (response.status.code !== 0) {
          throw new Error(response?.status?.description)
        }
        toggleFormUpdate()
        Swal.fire({
          icon: 'success',
          title: 'ยกเลิกสำเร็จ',
        })
        refetch()
      } catch (error) {
        handleError(error)
      } finally {
        setIsLoading(false)
      }
    },

    [refetch, toggleFormUpdate],
  )

  const handleError = (error) => {
    Swal.fire({
      icon: 'error',
      title: 'เกิดข้อผิดพลาด',
      text: error ?? 'เกิดข้อผิดพลาด',
    })
  }

  const headerTable = ['รูป', 'ชื่อ', 'รายละเอียด', 'ระยะเวลา', 'จำนวนชุด', 'Status', 'Actions']

  return (
    <>
      <YStack>
        <Card>
          <Card.Header>
            <Card.Title>ไฮไลท์ลอตเตอรี่</Card.Title>
            <Button className="w-25" size="sm" onClick={toggleFormCreate}>
              สร้าง
            </Button>
          </Card.Header>
          <Card.Body>
            {highlightSeriesList ? (
              <Table bordered>
                <thead>
                  <tr>
                    {headerTable.map((header) => (
                      <th key={`${header}`}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {highlightSeriesList?.map((item) => {
                    const info = JSON.parse(item.payload)
                    return (
                      <tr key={`${item.id}-${item.status}`}>
                        <Td>
                          {info?.HeaderImage && (
                            <Image src={info?.HeaderImage} style={{ borderRadius: 8, width: 140 }} />
                          )}
                        </Td>
                        <Td>{<Title>{info?.Title}</Title>}</Td>
                        <Td>{<Description>{info?.Description}</Description>}</Td>
                        <td>
                          {`${moment(info?.Start).format('DD/MM/YYYY HH:mm')} - 
                        ${moment(info?.End).format('DD/MM/YYYY HH:mm')}`}
                          <div>{`(${moment(info?.End).diff(moment(info?.Start), 'minutes')} นาที)`}</div>
                          <div> {<CountdownStatus status={info?.ShowCountdown} />}</div>
                        </td>

                        <td>{info?.Sizes?.toString()}</td>
                        <td>{<TagStatus status={item.status} />}</td>
                        <td>
                          {['DRAFT'].includes(item.status) && (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setHighlightId(item.id)
                                toggleFormUpdate()
                              }}
                            >
                              ยกเลิก
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            ) : null}
            <XStack style={{ justifyContent: 'flex-end' }}>
              {data?.pagination ? (
                <Pagination style={{ overflow: 'hidden' }}>
                  <Pagination.Item
                    onClick={() => {
                      pageParam > 1 && setPageParam((prevState) => prevState - 1)
                    }}
                    disabled={loading || pageParam === 1}
                    style={{ userSelect: 'none' }}
                  >
                    ‹
                  </Pagination.Item>
                  <Pagination.Item disabled>{pageParam}</Pagination.Item>
                  <Pagination.Item
                    onClick={() => {
                      setPageParam((prevState) => prevState + 1)
                    }}
                    disabled={loading || !data.pagination.hasNext}
                    style={{ userSelect: 'none' }}
                  >
                    ›
                  </Pagination.Item>
                </Pagination>
              ) : null}
            </XStack>
          </Card.Body>
        </Card>
      </YStack>
      <Modal size="xl" show={onFormCreate} onHide={toggleFormCreate} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title as={'h3'}>สร้างไฮไลท์ลอตเตอรี่</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormHighlightLottery
            handleSubmitForm={handleSubmitFormCreate}
            isLoading={isLoading}
            defaultValues={highlightSeriesList?.[0]}
          />
        </Modal.Body>
      </Modal>
      <Modal size="xl" show={onFormUpdate} onHide={toggleFormUpdate} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>ยกเลิกไฮไลท์ลอตเตอรี่</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormUpdateHighlightLottery
            handleSubmitForm={handleSubmitFormUpdate}
            isLoading={isLoading}
            defaultValues={{ id: highlightId }}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}
export default HighlightLottery
